import {
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  Link,
  Select,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { HTTPError } from "ky";
import { noop } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import * as Yup from "yup";

import {
  DEALER_ORGANIZATIONS_QUERY_KEY,
  useDealerOrganizationQueryData,
} from "src/routes/DealerOrganization/queries";
import { handleHookFormHTTPError } from "src/common/form";
import { useKy } from "src/common/ky";
import { YupPossiblePhoneNumber } from "src/common/phone-numbers";
import { PhoneNumberInput } from "src/common/PhoneNumbers";
import { PageContent } from "src/layout/PageContent";
import { DealerType, DEALER_TYPES } from "src/common/types";
import { USStates } from "src/common/USStates";

type DealerOrganizationFormValues = {
  name: string;
  dba: string;
  phone: string;
  website: string;
  pocFirstName: string;
  pocLastName: string;
  pocPhone: string;
  pocEmail: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  dealerType: DealerType;
  isDmpDealer: boolean;
};

const EditDealerOrganizationDetailsSchema = Yup.object().shape({
  name: Yup.string().required().label("Company Name"),
  dba: Yup.string().label("Company DBA"),
  phone: YupPossiblePhoneNumber({
    label: "Company Phone Number",
    required: true,
  }),
  website: Yup.string()
    .required()
    .matches(
      /((https?):\/\/)?(www.)?[a-z0-9-]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#-]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?/,
      "Please enter a valid url"
    )
    .label("Website"),
  pocFirstName: Yup.string().required().label("First Name"),
  pocLastName: Yup.string().required().label("Last Name"),
  pocPhone: YupPossiblePhoneNumber({ required: true }),
  pocEmail: Yup.string().email().required().label("Email"),
  addressLine1: Yup.string().required().label("Address Line 1"),
  addressLine2: Yup.string().optional().default("").label("Address Line 2"),
  city: Yup.string().required().label("City"),
  state: Yup.string().required().label("State"),
  zipCode: Yup.string().required().label("Zip Code"),
  dealerType: Yup.string().required().oneOf(DEALER_TYPES).label("Dealer Type"),
  isDmpDealer: Yup.boolean().required().label("DMP Dealer"),
});

export const EditDealerOrganizationDetails = () => {
  const { id } = useParams<"id">();
  const navigate = useNavigate();
  const ky = useKy();
  const toast = useToast();
  const queryClient = useQueryClient();

  const data = useDealerOrganizationQueryData(id);

  if (!data) return <></>;

  const {
    control,
    handleSubmit,
    setError,
    getValues,
    formState: { isSubmitting },
  } = useForm<DealerOrganizationFormValues>({
    defaultValues: {
      name: data.name,
      dba: data.dba,
      phone: data.phone,
      website: data.website,
      pocFirstName: data.pointOfContact.user.firstName,
      pocLastName: data.pointOfContact.user.lastName,
      pocPhone: data.pointOfContact.user.phone,
      pocEmail: data.pointOfContact.user.email,
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      dealerType: data.dealerType,
      isDmpDealer: data.isDmpDealer,
    },
    resolver: yupResolver(EditDealerOrganizationDetailsSchema),
  });

  const updateDealerProfile = useMutation<
    void,
    HTTPError,
    DealerOrganizationFormValues
  >(
    async (values: DealerOrganizationFormValues) => {
      await ky.patch(`dealer-organizations/${id}`, { json: values });
    },
    {
      onSuccess: async () => {
        toast({
          description: "Dealer Details Successfully Updated",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
        await queryClient.invalidateQueries(
          `${DEALER_ORGANIZATIONS_QUERY_KEY}:${id}`
        );
        navigate(`/dealers/${id}/details`);
      },

      onError: handleHookFormHTTPError(setError, getValues, toast),
    }
  );

  return (
    <>
      <form
        onSubmit={handleSubmit((data) =>
          updateDealerProfile.mutateAsync(data).catch(noop)
        )}
      >
        <PageContent>
          <Grid templateColumns={"repeat(2, 1fr)"} gap={4}>
            <GridItem colSpan={2}>
              <Heading size="md">Company Information</Heading>
            </GridItem>

            <GridItem colSpan={2}>
              <Divider />
            </GridItem>

            <GridItem>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="name">Company Name</FormLabel>
                    <Input
                      {...field}
                      id="name"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="dba"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="dba">Company DBA</FormLabel>
                    <Input
                      {...field}
                      id="dba"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="addressLine1"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="addressLine1">Address Line 1</FormLabel>
                    <Input
                      {...field}
                      id="addressLine1"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="addressLine2"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="addressLine2">Address Line 2</FormLabel>
                    <Input
                      {...field}
                      id="addressLine2"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="city"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="city">City</FormLabel>
                    <Input
                      {...field}
                      id="city"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="state"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="state">State</FormLabel>
                    <Select
                      width={["100%", "100%", "50%", "50%"]}
                      {...field}
                      id="state"
                    >
                      <option value="" />
                      {USStates.map((state) => (
                        <option
                          key={state.abbreviation}
                          value={state.abbreviation}
                        >
                          {state.name}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="zipCode"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="zipCode">Zip Code</FormLabel>
                    <Input
                      {...field}
                      id="zipCode"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="phone"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="phone">Phone Number</FormLabel>
                    <PhoneNumberInput {...field} id="phone" />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="website"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="website">Website</FormLabel>
                    <Input
                      {...field}
                      id="website"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="dealerType"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="dealerType">Dealer Type</FormLabel>
                    <Select
                      {...field}
                      id="dealerType"
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    >
                      <option value="Internal">Internal</option>
                      <option value="Billable">Billable</option>
                    </Select>
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="isDmpDealer"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isDisabled={isSubmitting}
                    isInvalid={fieldState.invalid}
                  >
                    <FormLabel htmlFor="isDmpDealer">DMP Dealer</FormLabel>
                    <Select
                      {...field}
                      id="isDmpDealer"
                      onChange={(event) => {
                        field.onChange(event.target.value === "1");
                      }}
                      value={field.value ? "1" : "0"}
                    >
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </Select>
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem colSpan={2}>
              <Divider />
            </GridItem>

            <GridItem colSpan={2}>
              <ButtonGroup display="flex" justifyContent="flex-end">
                <Link as={RouterLink} to="/dealers">
                  <Button>Cancel</Button>
                </Link>
                <Button
                  type="submit"
                  colorScheme="brand.blue"
                  isLoading={isSubmitting}
                >
                  Save Dealer
                </Button>
              </ButtonGroup>
            </GridItem>
          </Grid>
        </PageContent>
      </form>
    </>
  );
};
